import { CommonModule } from '@angular/common';
import { AuditDirectoryCellComponent } from '../../shared/audit/audit-directory-cell.component';
import { Component, OnInit, inject } from '@angular/core';
import { FormBuilder, ReactiveFormsModule, Validators } from '@angular/forms';
import { RouterModule } from '@angular/router';
import { MessageService } from 'primeng/api';
import { ButtonModule } from 'primeng/button';
import { IftaLabelModule } from 'primeng/iftalabel';
import { InputGroupModule } from 'primeng/inputgroup';
import { InputGroupAddonModule } from 'primeng/inputgroupaddon';
import { InputNumberModule } from 'primeng/inputnumber';
import { InputTextModule } from 'primeng/inputtext';
import { SelectModule } from 'primeng/select';
import { TableModule } from 'primeng/table';
import { TagModule } from 'primeng/tag';
import { ToastModule } from 'primeng/toast';
import { finalize } from 'rxjs';
import { PageDataPanelComponent } from '../../shared/page-layout/page-data-panel.component';
import { PageHeroComponent } from '../../shared/page-layout/page-hero.component';
import { PageShellComponent } from '../../shared/page-layout/page-shell.component';
import { WarehousingHubLane, WarehousingMasterData } from '../warehousing.model';
import { WarehousingService } from '../warehousing.service';

@Component({
  selector: 'app-warehousing-hub-lanes',
  standalone: true,
  imports: [AuditDirectoryCellComponent,
    CommonModule,
    ReactiveFormsModule,
    RouterModule,
    ButtonModule,
    IftaLabelModule,
    InputGroupModule,
    InputGroupAddonModule,
    InputNumberModule,
    InputTextModule,
    SelectModule,
    TableModule,
    TagModule,
    ToastModule,
    PageShellComponent,
    PageHeroComponent,
    PageDataPanelComponent
  ],
  providers: [MessageService],
  templateUrl: './warehousing-hub-lanes.component.html',
  styleUrls: ['./warehousing-hub-lanes.component.css']
})
export class WarehousingHubLanesComponent implements OnInit {
  private fb = inject(FormBuilder);
  private service = inject(WarehousingService);
  private messageService = inject(MessageService);

  rows: WarehousingHubLane[] = [];
  warehouses: WarehousingMasterData[] = [];
  isLoading = false;
  isSaving = false;
  editingId?: number;

  laneTypeOptions = [
    { label: 'Inter Hub', value: 'INTER_HUB' },
    { label: 'First Mile 3PL', value: 'FIRST_MILE_3PL' },
    { label: 'Final Mile Hub', value: 'FINAL_MILE_HUB' }
  ];

  form = this.fb.group({
    sourceWarehouseId: [null as number | null, Validators.required],
    destinationWarehouseId: [null as number | null, Validators.required],
    laneType: ['INTER_HUB', Validators.required],
    priority: [100, Validators.required],
    costRank: [100, Validators.required],
    distanceKm: [null as number | null],
    transitDurationMinutes: [240, Validators.required],
    crossDockDwellMinutes: [120, Validators.required],
    loadingMinutes: [30],
    unloadingMinutes: [30],
    cutoffBufferMinutes: [30],
    isActive: [true]
  });

  ngOnInit(): void {
    this.load();
  }

  load(): void {
    this.isLoading = true;
    this.service.getMasterData('WAREHOUSE', { activeStatus: 'ACTIVE' }).subscribe({
      next: warehouseResponse => {
        this.warehouses = warehouseResponse.data || [];
        this.service.getHubLanes().subscribe({
          next: laneResponse => {
            this.rows = laneResponse.data || [];
            this.isLoading = false;
          },
          error: () => {
            this.isLoading = false;
            this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load hub lanes.' });
          }
        });
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Load failed', detail: 'Could not load warehouses.' });
      }
    });
  }

  save(): void {
    if (this.isSaving) {
      return;
    }
    if (this.form.invalid) {
      this.form.markAllAsTouched();
      return;
    }
    const value = this.form.getRawValue();
    if (value.sourceWarehouseId === value.destinationWarehouseId) {
      this.messageService.add({ severity: 'warn', summary: 'Invalid lane', detail: 'Source and destination warehouses must be different.' });
      return;
    }
    const payload: WarehousingHubLane = {
      id: this.editingId,
      sourceWarehouseId: value.sourceWarehouseId!,
      destinationWarehouseId: value.destinationWarehouseId!,
      laneType: value.laneType || 'INTER_HUB',
      priority: Number(value.priority || 100),
      costRank: Number(value.costRank || 100),
      distanceKm: value.distanceKm ?? undefined,
      transitDurationMinutes: Number(value.transitDurationMinutes || 0) || undefined,
      crossDockDwellMinutes: Number(value.crossDockDwellMinutes || 0),
      loadingMinutes: Number(value.loadingMinutes || 0),
      unloadingMinutes: Number(value.unloadingMinutes || 0),
      cutoffBufferMinutes: Number(value.cutoffBufferMinutes || 0),
      isActive: value.isActive !== false
    };
    this.isLoading = true;
    this.isSaving = true;
    this.service.saveHubLane(payload).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Hub lane saved', detail: 'Cross-dock lane topology was updated.' });
        this.resetForm();
        this.load();
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Save failed', detail: 'Could not save hub lane.' });
      }
    });
  }

  edit(row: WarehousingHubLane): void {
    this.editingId = row.id;
    this.form.patchValue({
      sourceWarehouseId: row.sourceWarehouseId || null,
      destinationWarehouseId: row.destinationWarehouseId || null,
      laneType: row.laneType || 'INTER_HUB',
      priority: row.priority || 100,
      costRank: row.costRank || 100,
      distanceKm: row.distanceKm ?? null,
      transitDurationMinutes: row.transitDurationMinutes || 240,
      crossDockDwellMinutes: row.crossDockDwellMinutes ?? 120,
      loadingMinutes: row.loadingMinutes ?? 30,
      unloadingMinutes: row.unloadingMinutes ?? 30,
      cutoffBufferMinutes: row.cutoffBufferMinutes ?? 30,
      isActive: row.isActive !== false
    });
  }

  deactivate(row: WarehousingHubLane): void {
    if (!row.id || this.isSaving) return;
    this.isLoading = true;
    this.isSaving = true;
    this.service.deactivateHubLane(row.id).pipe(finalize(() => this.isSaving = false)).subscribe({
      next: () => {
        this.messageService.add({ severity: 'success', summary: 'Hub lane deactivated', detail: 'Lane is no longer active for route resolution.' });
        this.load();
      },
      error: () => {
        this.isLoading = false;
        this.messageService.add({ severity: 'error', summary: 'Deactivate failed', detail: 'Could not deactivate hub lane.' });
      }
    });
  }

  resetForm(): void {
    this.editingId = undefined;
    this.form.reset({
      sourceWarehouseId: null,
      destinationWarehouseId: null,
      laneType: 'INTER_HUB',
      priority: 100,
      costRank: 100,
      distanceKm: null,
      transitDurationMinutes: 240,
      crossDockDwellMinutes: 120,
      loadingMinutes: 30,
      unloadingMinutes: 30,
      cutoffBufferMinutes: 30,
      isActive: true
    });
  }

  warehouseName(id?: number): string {
    return this.warehouses.find(row => row.id === id)?.name || (id ? `Warehouse #${id}` : '-');
  }

  laneTypeLabel(value?: string): string {
    return this.laneTypeOptions.find(option => option.value === value)?.label || value || '-';
  }

  statusSeverity(active?: boolean): 'success' | 'danger' {
    return active === false ? 'danger' : 'success';
  }
}
